import React, { useEffect, useRef, useState } from 'react';

// Scan a serial-number barcode with the phone camera.
//
// Two engines, in order of preference:
//   1. The browser's built-in BarcodeDetector — fastest, no download. Ships on
//      Android, ChromeOS and macOS Chrome.
//   2. ZXing, loaded only when needed. Covers iPhone and Chrome on Windows,
//      where BarcodeDetector does not exist.
//
// The library is behind a dynamic import so it is a separate chunk that is only
// fetched the first time somebody actually opens the camera — the rest of the
// portal is not made heavier for a feature most people use rarely.
//
// So the button now shows wherever there is a camera, rather than only where
// the native detector happens to exist.
export const barcodeSupported = () =>
  typeof navigator !== 'undefined' &&
  navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function';

const NATIVE_FORMATS = ['code_128', 'code_39', 'code_93', 'codabar',
                        'ean_13', 'ean_8', 'itf', 'upc_a', 'upc_e', 'qr_code'];

export default function BarcodeScanner({ onFound, onClose }) {
  const video = useRef(null);
  const stream = useRef(null);
  const controls = useRef(null);      // ZXing's stop handle
  const stop = useRef(false);
  const [err, setErr] = useState('');
  const [engine, setEngine] = useState('');
  const [torch, setTorch] = useState(null);
  const [res, setRes] = useState('');        // actual camera resolution
  const [slow, setSlow] = useState(false);   // nothing read after a while

  // If nothing has decoded after 10 seconds, stop staring silently and say what
  // usually fixes it.
  useEffect(() => {
    const t = setTimeout(() => { if (!stop.current) setSlow(true); }, 10000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const hit = value => {
      const v = String(value || '').trim();
      if (!v || stop.current) return;
      stop.current = true;
      onFound(v);
    };

    (async () => {
      try {
        // Resolution is the whole game for a 1D barcode. getUserMedia defaults
        // to about 640x480, at which the bars of a Code 128 serial blur into
        // each other and nothing ever decodes — the camera looks like it is
        // working while never reading anything. Ask for as much detail as the
        // device will give, and for continuous autofocus.
        const want = res => ({
          video: {
            facingMode: { ideal: 'environment' },
            width:  { ideal: res }, height: { ideal: Math.round(res * 9 / 16) },
            advanced: [{ focusMode: 'continuous' }],
          }, audio: false,
        });
        try { stream.current = await navigator.mediaDevices.getUserMedia(want(1920)); }
        catch (e) {
          try { stream.current = await navigator.mediaDevices.getUserMedia(want(1280)); }
          catch (e2) {
            stream.current = await navigator.mediaDevices.getUserMedia(
              { video: { facingMode: { ideal: 'environment' } }, audio: false });
          }
        }
        if (!video.current) return;
        video.current.srcObject = stream.current;
        await video.current.play();

        const track = stream.current.getVideoTracks()[0];
        if (track.getCapabilities && track.getCapabilities().torch) setTorch(false);
        // Shown on screen: if scanning still fails, the resolution the camera
        // actually gave us is the first thing worth knowing.
        try {
          const s = track.getSettings();
          if (s.width && s.height) setRes(`${s.width}×${s.height}`);
        } catch (e) { /* not all browsers report settings */ }

        if ('BarcodeDetector' in window) {
          setEngine('native');
          const supported = await window.BarcodeDetector.getSupportedFormats();
          const detector = new window.BarcodeDetector({
            formats: NATIVE_FORMATS.filter(f => supported.includes(f)) });
          const tick = async () => {
            if (stop.current || !video.current) return;
            try {
              const codes = await detector.detect(video.current);
              if (codes && codes.length) return hit(codes[0].rawValue);
            } catch (e) { /* a bad frame — keep looking */ }
            setTimeout(tick, 250);
          };
          tick();
          return;
        }

        // Fallback: fetch the decoder only now.
        setEngine('library');
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] =
          await Promise.all([import('@zxing/browser'), import('@zxing/library')]);
        if (stop.current) return;
        // Telling it which formats to expect, and to try hard, makes a real
        // difference on 1D codes — left to guess across every format it often
        // never settles on one.
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.CODE_93,
          BarcodeFormat.CODABAR, BarcodeFormat.ITF,
          BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
          BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
          BarcodeFormat.QR_CODE, BarcodeFormat.DATA_MATRIX,
        ]);
        hints.set(DecodeHintType.TRY_HARDER, true);
        const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 100 });
        // decodeFromStream, not decodeFromVideoElement: we own the stream and
        // its high-resolution constraints, and want the decoder to use it.
        controls.current = await reader.decodeFromStream(
          stream.current, video.current, (result) => { if (result) hit(result.getText()); });
      } catch (e) {
        setErr(e && e.name === 'NotAllowedError'
          ? 'Camera permission was refused. You can type the serial instead.'
          : 'Could not start the camera. You can type the serial instead.');
      }
    })();

    return () => {
      stop.current = true;
      try { controls.current && controls.current.stop(); } catch (e) {}
      if (stream.current) stream.current.getTracks().forEach(t => t.stop());
    };
  }, [onFound]);

  const toggleTorch = async () => {
    try {
      const track = stream.current.getVideoTracks()[0];
      await track.applyConstraints({ advanced: [{ torch: !torch }] });
      setTorch(t => !t);
    } catch (e) { /* some devices refuse mid-stream */ }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:460 }}>
        <div className="modal-header">
          <span className="modal-title">Scan the barcode</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding:14 }}>
          {err ? (
            <div style={{ fontSize:13, color:'var(--red)', marginBottom:12 }}>{err}</div>
          ) : (
            <>
              <div style={{ position:'relative', background:'#000', borderRadius:10, overflow:'hidden' }}>
                <video ref={video} playsInline muted
                  style={{ width:'100%', maxHeight:'55vh', display:'block', objectFit:'cover' }} />
                {/* A window to aim through — barcodes are wide and short. */}
                <div style={{ position:'absolute', inset:'32% 10%', border:'2px solid rgba(255,255,255,.85)',
                  borderRadius:8, boxShadow:'0 0 0 100vmax rgba(0,0,0,.35)' }} />
              </div>
              <div style={{ fontSize:12, color:'var(--text2)', marginTop:9, textAlign:'center' }}>
                {engine ? 'Hold the serial-number barcode inside the box.' : 'Starting the camera…'}
                {res && <span style={{ opacity:.6 }}> · {res} · {engine === 'native' ? 'built-in' : 'ZXing'}</span>}
              </div>
              {slow && (
                <div style={{ background:'#FDF0CD', border:'1px solid #E8CE86', borderRadius:8,
                  padding:'9px 11px', marginTop:9, fontSize:12, color:'#7A5300' }}>
                  Still not reading? Fill the box with the barcode — about a hand's
                  width away — hold steady, and turn the light on. If the sticker is
                  scratched or very small, type the serial instead.
                </div>
              )}
            </>
          )}
          <div className="flex-gap" style={{ gap:8, marginTop:12, justifyContent:'center' }}>
            {torch !== null && (
              <button className="btn btn-secondary btn-sm" onClick={toggleTorch}>
                {torch ? '🔦 Light off' : '🔦 Light on'}
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
